import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { api } from '../../api/client';

interface Stats {
  users: { role: string; count: number }[];
  enrolments: { status: string; count: number }[];
  assessments_total: number;
  cv_reviews: { status: string; count: number }[];
}

export function AdminDashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<Stats>('/admin/stats')
      .then(setStats)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const getUserCount = (role: string) => stats?.users.find((u) => u.role === role)?.count || 0;
  const getEnrolmentCount = (status: string) => stats?.enrolments.find((e) => e.status === status)?.count || 0;
  const totalUsers = stats?.users.reduce((acc, u) => acc + u.count, 0) || 0;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto mt-10 px-6 pb-20">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold text-mustard-green-dark">Admin Dashboard</h1>
            <p className="text-mustard-charcoal/60 mt-1">Platform overview and management</p>
          </div>
        </div>

        {loading ? (
          <p className="text-mustard-charcoal/60">Loading...</p>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              <StatCard label="Total Users" value={totalUsers} />
              <StatCard label="Candidates" value={getUserCount('candidate')} />
              <StatCard label="Trainers" value={getUserCount('trainer')} />
              <StatCard label="Assessments" value={stats?.assessments_total || 0} />
              <StatCard label="Active Enrolments" value={getEnrolmentCount('active')} accent />
              <StatCard label="Completed" value={getEnrolmentCount('completed')} />
              <StatCard label="CV Reviews" value={stats?.cv_reviews.reduce((a, r) => a + r.count, 0) || 0} />
              <StatCard label="Admins" value={getUserCount('admin')} />
            </div>

            {/* Quick Links */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <AdminLink to="/admin/users" title="User Management" desc="View, create, and manage users" />
              <AdminLink to="/admin/enrolments" title="Enrolments" desc="Programme enrolments and progression" />
              <AdminLink to="/admin/analytics" title="Analytics" desc="Assessment funnels, completion rates" />
              <AdminLink to="/admin/trainers" title="Trainers" desc="Certification and workload management" />
              <AdminLink to="/admin/audit-log" title="Audit Log" desc="Track all admin actions" />
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

function StatCard({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className={`rounded-xl p-5 shadow-sm border ${accent ? 'bg-mustard-green-dark text-white border-mustard-green-dark' : 'bg-white border-gray-100'}`}>
      <p className={`text-sm ${accent ? 'text-white/70' : 'text-mustard-charcoal/50'}`}>{label}</p>
      <p className={`text-3xl font-bold font-display mt-1 ${accent ? '' : 'text-mustard-green-dark'}`}>{value}</p>
    </div>
  );
}

function AdminLink({ to, title, desc }: { to: string; title: string; desc: string }) {
  return (
    <Link to={to} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:border-mustard-green-mid transition-colors">
      <h3 className="font-display font-bold text-mustard-green-dark mb-1">{title}</h3>
      <p className="text-sm text-mustard-charcoal/50">{desc}</p>
    </Link>
  );
}
